import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import FilterBar from '@/components/feature/FilterBar';
import VideoGrid from '@/components/feature/VideoGrid';
import Pagination from '@/components/feature/Pagination';
import { useVideoFilter, defaultFilterState } from '@/hooks/useVideoFilter';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(initial);
  const [filters, setFilters] = useState(defaultFilterState);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(params.get('q') ?? '');
    setPage(1);
  }, [params]);

  const source = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter(
      (v) => v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q) || v.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [keyword]);

  const subCategories = useMemo(() => {
    const names = Array.from(new Set(source.map((v) => v.subCategory)));
    return names.map((n) => ({ value: n, label: n }));
  }, [source]);

  const filtered = useVideoFilter(source, filters);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageVideos = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const popular = useMemo(() => [...videos].sort((a, b) => b.views - a.views).slice(0, 3), []);

  const updateFilter = (patch: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParams(keyword.trim() ? { q: keyword.trim() } : {});
    setPage(1);
  };

  useDocumentTitle(
    keyword ? `搜索“${keyword}” - 靠逼视频` : '视频搜索 - 靠逼视频',
    keyword
      ? `在靠逼视频搜索“${keyword}”相关的视频内容。`
      : '在靠逼视频搜索你感兴趣的娱乐、生活、搞笑等视频内容。',
  );

  const noResult = filtered.length === 0;

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-up">
      <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-600 mb-5" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">搜索结果</span>
      </nav>

      <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
        {keyword ? (
          <>
            搜索“<span className="text-primary-400">{keyword}</span>”的结果
          </>
        ) : (
          '搜索视频'
        )}
      </h1>

      <form onSubmit={submit} className="mt-4 md:mt-5 max-w-2xl">
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入视频标题、简介或标签关键词"
            className="w-full h-12 pl-11 pr-28 rounded-full bg-background-100 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>
      </form>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-600 mr-1">按分类快速切换：</span>
        {categories.map((c) => (
          <Link
            key={c.id}
            to={`/category/${c.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-400 hover:text-foreground-950 transition-colors"
          >
            <i className={c.icon}></i>
            {c.name}
          </Link>
        ))}
      </div>

      {!noResult && (
        <>
          <div className="mt-6 bg-background-100 border border-background-200 rounded-lg p-4 md:p-5">
            <FilterBar
              subCategories={subCategories}
              subCategory={filters.subCategory}
              onSubCategory={(v) => updateFilter({ subCategory: v })}
              time={filters.time}
              onTime={(v) => updateFilter({ time: v })}
              duration={filters.duration}
              onDuration={(v) => updateFilter({ duration: v })}
              sort={filters.sort}
              onSort={(v) => updateFilter({ sort: v })}
            />
          </div>

          <p className="mt-5 mb-4 text-sm text-foreground-600">
            共找到 <strong className="text-foreground-950 font-semibold">{filtered.length}</strong> 个相关视频
          </p>

          <VideoGrid videos={pageVideos} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />
          <Pagination current={current} total={totalPages} onChange={setPage} />
        </>
      )}

      {noResult && (
        <div className="mt-6">
          <div className="text-center py-14 bg-background-100 border border-background-200 rounded-lg">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
              <i className="ri-search-eye-line"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有找到相关视频</h2>
            <p className="mt-2 text-sm text-foreground-600">换一个关键词试试，或者看看下面的热门推荐。</p>
          </div>

          <div className="mt-8">
            <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-4">热门视频推荐</h3>
            <VideoGrid videos={popular} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />
          </div>
        </div>
      )}
    </div>
  );
}